/**
 * Shared cold-send cap model — the SINGLE source of truth for per-mailbox
 * daily cold-send PROSPECT caps by reputation-age tier.
 *
 * This lives in `shared/` (not in `server/lib/email/cold-send-policy.ts`) so the
 * client UI (e.g. the Cold Email Health card's send-cap guide) and the server
 * enforcement path read the exact same numbers and can never drift. The server
 * policy module re-exports these so existing server imports keep working.
 */

/** Reputation-age tiers for a cold mailbox. `seasoning` is the pre-sale
 * conditioning window (no prospect outreach at all); the remaining bands ramp
 * the per-mailbox prospect budget as the box ages cleanly. */
export type ColdTier = "seasoning" | "new" | "warming" | "established" | "warmed";

/** The single source of truth for per-mailbox cold-send daily PROSPECT caps by
 * tier. Conservative on purpose: a warmed box tops out at ~100/day, and a box
 * still in seasoning sends ZERO prospect mail. */
export const COLD_SEND_TIER_CAPS: Record<ColdTier, number> = {
  seasoning: 0,
  new: 15,
  warming: 35,
  established: 60,
  warmed: 100,
};

/** Daily allowance for inbox-placement test sends (real sends to a seed list).
 * Available in EVERY band — including seasoning — because placement testing is
 * how a box earns its way out of seasoning. Kept small (≈ one seed list). */
export const PLACEMENT_TEST_DAILY_BUDGET = 30;

/**
 * Ordered display metadata for the four ramping (non-seasoning) tiers, used by
 * UI surfaces that explain the caps to users. The `cap` is sourced from
 * `COLD_SEND_TIER_CAPS` so copy and enforcement stay in lockstep. Seasoning is
 * intentionally excluded here (it's a lifecycle band, not an age band) and is
 * called out separately as the zero-prospect case.
 */
export interface ColdTierGuideEntry {
  tier: Exclude<ColdTier, "seasoning">;
  label: string;
  /** Human-readable age window for the tier. */
  dayRange: string;
  /** Base daily prospect cap before the reputation-score modifier. */
  cap: number;
}

export const COLD_SEND_TIER_GUIDE: ColdTierGuideEntry[] = [
  { tier: "new", label: "New", dayRange: "days 0–29", cap: COLD_SEND_TIER_CAPS.new },
  { tier: "warming", label: "Warming", dayRange: "days 30–59", cap: COLD_SEND_TIER_CAPS.warming },
  { tier: "established", label: "Established", dayRange: "days 60–89", cap: COLD_SEND_TIER_CAPS.established },
  { tier: "warmed", label: "Warmed", dayRange: "90+ days clean", cap: COLD_SEND_TIER_CAPS.warmed },
];

/**
 * The warm-up GRADUATION GATE — the two conditions a mailbox must meet before
 * it exits seasoning and may send any prospect email. Shared so UI surfaces
 * (e.g. the lease wizard's warm-up status step) display the exact gate the
 * warmup scheduler enforces, rather than an age counter that implies readiness.
 * The scheduler still honors WARMUP_MIN_DAYS / WARMUP_GRADUATE_SCORE env
 * overrides; these are the defaults both sides agree on.
 */
export const WARMUP_GRADUATION_GATE = {
  /** Instantly warmup health score required to graduate (0–100). */
  minHealthScore: 70,
  /** Minimum days since warmup enrollment required to graduate. */
  minDays: 10,
} as const;

/**
 * Classify a clean-age day count into its ramp tier guide entry. Mirrors the
 * server enforcement bands in `reputationTierDailyCap` (cold-send-policy) so a
 * display surface computing "current cap tier" from a clean-age anchor can
 * never drift from the enforced numbers.
 */
export function coldTierForCleanDays(days: number): ColdTierGuideEntry {
  if (days >= 90) return COLD_SEND_TIER_GUIDE[3];
  if (days >= 60) return COLD_SEND_TIER_GUIDE[2];
  if (days >= 30) return COLD_SEND_TIER_GUIDE[1];
  return COLD_SEND_TIER_GUIDE[0];
}
